"use client";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Check,
  Circle,
  Loader2,
  MapPin,
  Navigation,
  Package,
  Warehouse,
  Truck,
  ShieldCheck,
} from "lucide-react";

interface ShipmentStepperProps {
  type: string;
  status: string;
  otpVerified: boolean;
  onStatusChange: (toStatus: string) => void;
  isUpdating: boolean;
  disabled?: boolean;
}

const pickupSteps = [
  { status: "SCHEDULED", label: "Scheduled", icon: Circle },
  { status: "ASSIGNED", label: "Assigned", icon: Package },
  { status: "EN_ROUTE", label: "En Route", icon: Navigation },
  { status: "ARRIVED", label: "Arrived", icon: MapPin },
  { status: "COLLECTED", label: "Collected", icon: Check },
  { status: "HANDED_TO_WORKSHOP", label: "Handed Over", icon: Warehouse },
];

const deliverySteps = [
  { status: "SCHEDULED", label: "Scheduled", icon: Circle },
  { status: "ASSIGNED", label: "Assigned", icon: Package },
  { status: "EN_ROUTE", label: "En Route", icon: Navigation },
  { status: "ARRIVED", label: "Arrived", icon: MapPin },
  { status: "OUT_FOR_DELIVERY", label: "Out for Delivery", icon: Truck },
  { status: "DELIVERED", label: "Delivered", icon: Check },
];

const statusOrder: Record<string, number> = {
  SCHEDULED: 0,
  ASSIGNED: 1,
  EN_ROUTE: 2,
  ARRIVED: 3,
  COLLECTED: 4,
  OUT_FOR_DELIVERY: 4,
  HANDED_TO_WORKSHOP: 5,
  DELIVERED: 5,
};

export function ShipmentStepper({
  type,
  status,
  otpVerified,
  onStatusChange,
  isUpdating,
  disabled,
}: ShipmentStepperProps) {
  const steps = type === "PICKUP" ? pickupSteps : deliverySteps;
  const currentIndex = statusOrder[status] ?? 0;

  const getNextStatus = (): string | null => {
    const currentStep = steps.findIndex((s) => s.status === status);
    if (currentStep < 0 || currentStep >= steps.length - 1) return null;

    const nextStep = steps[currentStep + 1];

    if (type === "PICKUP" && nextStep.status === "COLLECTED" && !otpVerified) {
      return null;
    }

    if (type !== "PICKUP" && nextStep.status === "DELIVERED" && !otpVerified) {
      return null;
    }

    return nextStep.status;
  };

  const nextStatus = getNextStatus();
  const nextStep = nextStatus ? steps.find((s) => s.status === nextStatus) : null;

  const requiresOtpForNext =
    (type === "PICKUP" && status === "ARRIVED" && !otpVerified) ||
    (type !== "PICKUP" && status === "ARRIVED" && !otpVerified);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between overflow-x-auto pb-2">
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          const isCompleted = statusOrder[step.status] < currentIndex;
          const isCurrent = step.status === status;

          return (
            <div key={step.status} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors",
                    isCompleted
                      ? "bg-primary border-primary text-primary-foreground"
                      : isCurrent
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-muted bg-muted/50 text-muted-foreground"
                  )}
                >
                  {isCompleted ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <StepIcon className="h-5 w-5" />
                  )}
                </div>
                <span
                  className={cn(
                    "mt-2 text-xs font-medium whitespace-nowrap",
                    isCurrent ? "text-primary" : "text-muted-foreground"
                  )}
                >
                  {step.label}
                </span>
              </div>

              {index < steps.length - 1 && (
                <div
                  className={cn(
                    "h-0.5 w-8 mx-2",
                    statusOrder[step.status] < currentIndex
                      ? "bg-primary"
                      : "bg-muted"
                  )}
                />
              )}
            </div>
          );
        })}
      </div>

      {requiresOtpForNext && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
          <ShieldCheck className="h-5 w-5" />
          <span className="text-sm">
            OTP verification required before proceeding to next step
          </span>
        </div>
      )}

      {nextStatus && nextStep && (
        <Button
          onClick={() => onStatusChange(nextStatus)}
          disabled={isUpdating || disabled || requiresOtpForNext}
          className="w-full"
        >
          {isUpdating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Updating...
            </>
          ) : (
            <>
              Mark as {nextStep.label}
            </>
          )}
        </Button>
      )}

      {status === "COLLECTED" && type === "PICKUP" && (
        <Button
          onClick={() => onStatusChange("HANDED_TO_WORKSHOP")}
          disabled={isUpdating || disabled}
          className="w-full"
        >
          {isUpdating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <Warehouse className="h-4 w-4 mr-2" />
              Hand Over to Workshop
            </>
          )}
        </Button>
      )}
    </div>
  );
}
